import { useCallback, useMemo } from 'react';
import isEqual from 'react-fast-compare';
import { useFormContext } from 'react-hook-form';
import { useToggle } from 'react-use';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useEventHubContext } from 'src/contexts/EventHubContext';
import { useInventoryEventDetailContext } from 'src/contexts/InventoryEventDetailContext/InventoryEventDetailContext';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { isSuccess } from 'src/utils/errorUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ActionOutboxEntityType,
  Feature,
  ListingClient,
  ListingDetails,
  ListingDetailsAutoPricingSectionUpdates,
  UiCurrency,
} from 'src/WebApiController';

import { PricingSettingAutoPriceBody } from './PricingSettingAutoPriceBody';

type PricingSettingAutoPriceListingProps = {
  uiCurrency: UiCurrency;
  sellerFee: number | null;
  useVerticalView: boolean;
};

export const PricingSettingAutoPriceListing = ({
  uiCurrency,
  sellerFee,
  useVerticalView,
}: PricingSettingAutoPriceListingProps) => {
  const { watch, reset, handleSubmit, formState } =
    useFormContext<ListingDetailsAutoPricingSectionUpdates>();
  const { event, selectedListing, sectionRowScoreRecommendations } =
    useInventoryEventDetailContext();

  const hasSelectedSectionScoreRecommendationsFeature = useUserHasFeature(
    Feature.SelectedSectionScoreRecommendations
  );

  const { activeAccountWebClientConfig } = useAppContext();
  const { showErrorDialog } = useClientLogContext();
  const { updateActivePosEntityInfo } =
    useActivePosEntityContext<ListingDetails>();
  const { onUpdateListing } = useEventHubContext();
  const { refreshEventForItems } = useCatalogDataContext();

  const [isLoading, setLoading] = useToggle(false);

  const onSubmitStart = useCallback(() => {
    setLoading(true);
  }, [setLoading]);

  const onSubmitDone = useCallback(
    async (success = true) => {
      if (
        activeAccountWebClientConfig?.activeAccountId &&
        selectedListing?.id &&
        success
      ) {
        const updatedListing = await new ListingClient(
          activeAccountWebClientConfig
        ).getListingByListingId(selectedListing.id, undefined, undefined);

        if (updatedListing) {
          onUpdateListing(updatedListing.id);
          updateActivePosEntityInfo({
            event,
            posEntity: updatedListing,
            posEntityId: updatedListing.id,
            posEntityDisplayId: updatedListing.idOnMkp,
          });

          // If the listing belongs to a group, refresh the event data to ge
          if (selectedListing.ltGrpId) {
            refreshEventForItems(
              [selectedListing.id],
              ActionOutboxEntityType.Listing
            );
          }
        }
      }
      setLoading(false);
    },
    [
      activeAccountWebClientConfig,
      event,
      onUpdateListing,
      refreshEventForItems,
      selectedListing?.id,
      selectedListing?.ltGrpId,
      setLoading,
      updateActivePosEntityInfo,
    ]
  );

  const onSubmitHandler = useCallback(
    async (autoPricingSectionForm: ListingDetailsAutoPricingSectionUpdates) => {
      tryInvokeApi(
        async () => {
          onSubmitStart();
          if (hasSelectedSectionScoreRecommendationsFeature) {
            Object.assign(autoPricingSectionForm, {
              sectionRowScoreRecommendations: sectionRowScoreRecommendations,
            } as Partial<ListingDetailsAutoPricingSectionUpdates>);
          }
          const result = await new ListingClient(
            activeAccountWebClientConfig
          ).updateListingAutoPricingSectionByListingId(
            autoPricingSectionForm.id,
            autoPricingSectionForm
          );

          if (isSuccess(result)) {
            // Reset the form to latest changed
            reset(autoPricingSectionForm);
            await onSubmitDone();
          } else {
            showErrorDialog(
              'ListingClient.updateListingAutoPricingSection',
              { message: result.message, status: result.status },
              {
                trackErrorData: {
                  autoPricingSectionForm,
                },
              }
            );
          }
        },
        (error) => {
          showErrorDialog(
            'ListingClient.updateListingAutoPricingSection',
            error,
            {
              trackErrorData: autoPricingSectionForm,
            }
          );
        },
        () => {
          onSubmitDone(false);
        }
      );
    },
    [
      activeAccountWebClientConfig,
      hasSelectedSectionScoreRecommendationsFeature,
      onSubmitDone,
      onSubmitStart,
      reset,
      sectionRowScoreRecommendations,
      showErrorDialog,
    ]
  );

  const onSubmit = useCallback(() => {
    handleSubmit(onSubmitHandler)();
  }, [handleSubmit, onSubmitHandler]);

  const onReset = useCallback(() => {
    reset({ ...(formState.defaultValues ?? {}) });
  }, [formState.defaultValues, reset]);

  const input = watch();

  const hasChanges = useMemo(() => {
    const defaultValues = formState.defaultValues;
    return (
      input.netProceedsFloor !== defaultValues?.netProceedsFloor ||
      input.netProceedsCeiling !== defaultValues?.netProceedsCeiling ||
      input.currencyCode !== defaultValues?.currencyCode ||
      input.autoPricingEnabled !== defaultValues?.autoPricingEnabled ||
      input.compListingFloor !== defaultValues?.compListingFloor ||
      input.compListingCeiling !== defaultValues?.compListingCeiling ||
      input.compListingMode !== defaultValues?.compListingMode ||
      input.compListingOnlyForSameZoneEnabled !==
        defaultValues?.compListingOnlyForSameZoneEnabled ||
      input.compListingOnlyForSelectedSectionsEnabled !==
        defaultValues?.compListingOnlyForSelectedSectionsEnabled ||
      input.compListingExcludeFanInventory !==
        defaultValues?.compListingExcludeFanInventory ||
      input.compListingExcludeDefects !==
        defaultValues?.compListingExcludeDefects ||
      input.compListingIncludeUnmappedInventory !==
        defaultValues?.compListingIncludeUnmappedInventory ||
      input.compListingQuantityScoreAdjustmentEnabled !==
        defaultValues?.compListingQuantityScoreAdjustmentEnabled ||
      input.compListingQuantityScoreAdjustmentOverrideJson !==
        defaultValues?.compListingQuantityScoreAdjustmentOverrideJson ||
      input.undercutMode !== defaultValues?.undercutMode ||
      input.undercutReferenceListingPosition !==
        defaultValues?.undercutReferenceListingPosition ||
      input.undercutAbsoluteAmount !== defaultValues?.undercutAbsoluteAmount ||
      input.undercutRelativeAmount !== defaultValues?.undercutRelativeAmount ||
      input.circuitBreakerMaxDiscountVelocityPercent !==
        defaultValues?.circuitBreakerMaxDiscountVelocityPercent ||
      input.circuitBreakerMaxDiscountVelocityTicksInHours !==
        defaultValues?.circuitBreakerMaxDiscountVelocityTicksInHours ||
      input.circuitBreakerMinCompListingCount !==
        defaultValues?.circuitBreakerMinCompListingCount ||
      input.circuitBreakerRelativeCeiling !==
        defaultValues?.circuitBreakerRelativeCeiling ||
      input.circuitBreakerRelativeFloor !==
        defaultValues?.circuitBreakerRelativeFloor ||
      input.outlierMode !== defaultValues?.outlierMode ||
      input.outlierStandardDeviations !==
        defaultValues?.outlierStandardDeviations ||
      input.outlierKthLowestLimit !== defaultValues?.outlierKthLowestLimit ||
      input.outlierKthLowestLimitRelativeSpacing !==
        defaultValues?.outlierKthLowestLimitRelativeSpacing ||
      input.outlierKthLowestLimitAbsoluteSpacing !==
        defaultValues?.outlierKthLowestLimitAbsoluteSpacing ||
      input.skipAdvancePricing !== defaultValues?.skipAdvancePricing ||
      input.skipAutoPricing !== defaultValues?.skipAutoPricing ||
      input.skipCompListing !== defaultValues?.skipCompListing ||
      input.skipUndercut !== defaultValues?.skipUndercut ||
      !isEqual(
        input.compListingSelectedSectionSettings,
        defaultValues?.compListingSelectedSectionSettings
      ) ||
      !isEqual(
        input.compListingQuantityFilters,
        defaultValues?.compListingQuantityFilters
      )
    );
  }, [input, formState.defaultValues]);

  const hasSelectedSections = useMemo(() => {
    return (
      (input.compListingSelectedSectionSettings?.sectionIdFilter?.length ?? 0) >
      0
    );
  }, [input.compListingSelectedSectionSettings]);

  const {
    undercutMode,
    undercutRelativeAmount,
    undercutAbsoluteAmount,
    compListingMode,
    outlierMode,
    outlierStandardDeviations,
    outlierKthLowestLimit,
    outlierKthLowestLimitAbsoluteSpacing,
    outlierKthLowestLimitRelativeSpacing,
  } = input;

  return (
    <PricingSettingAutoPriceBody
      isPriceStaggerEnabled={false}
      compListingMode={compListingMode}
      outlierMode={outlierMode}
      undercutMode={undercutMode}
      undercutRelativeAmount={undercutRelativeAmount}
      undercutAbsoluteAmount={undercutAbsoluteAmount}
      outlierStandardDeviations={outlierStandardDeviations}
      outlierKthLowestLimit={outlierKthLowestLimit}
      outlierKthLowestLimitAbsoluteSpacing={
        outlierKthLowestLimitAbsoluteSpacing
      }
      outlierKthLowestLimitRelativeSpacing={
        outlierKthLowestLimitRelativeSpacing
      }
      hasChanges={hasChanges}
      hasSelectedSections={hasSelectedSections}
      isLoading={isLoading}
      uiCurrency={uiCurrency}
      sellerFee={sellerFee}
      useVerticalView={useVerticalView}
      onReset={onReset}
      onSubmit={onSubmit}
    />
  );
};
